// TopbarXL - Sprint 44 XL.
// Barra superior del shell XL. Titulo dinamico + breadcrumbs + acciones:
// toggle tema, CompanySelector (legacy), XLAvatar con menu (perfil/logout).
//
// No se toca el CompanySelector legacy aun: se reusa tal cual para no
// romper multi-tenant. En Fase 3 (migracion vistas) se puede reemplazar
// por un CompanyPickerXL si hace falta.
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ensureKeyframes, injectCSS } from '../../design/motion';
import { useXLTheme } from '../../design/tokens';
import XLBreadcrumbs from '../../components/xl/XLBreadcrumbs';
import XLAvatar from '../../components/xl/XLAvatar';
import XLTooltip from '../../components/xl/XLTooltip';
import CompanySelector from '../CompanySelector';
import useAuth from '../../hooks/useAuth';

const CSS_ID = 'xl-topbar-css';
const CSS = `
.xl-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 20px;
  min-height: 64px;
  background: var(--xl-color-bg);
  border-bottom: 1px solid var(--xl-color-border);
  font-family: var(--xl-font-sans);
  color: var(--xl-color-text);
  box-sizing: border-box;
}
.xl-top__left {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.xl-top__title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--xl-color-text);
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.xl-top__right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}
.xl-top__iconbtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  min-width: 36px;
  padding: 0 10px;
  border-radius: var(--xl-radius-md);
  border: 1px solid var(--xl-color-border);
  background: var(--xl-color-bg);
  color: var(--xl-color-text-secondary);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background 120ms cubic-bezier(0,0,0.2,1),
              border-color 120ms cubic-bezier(0,0,0.2,1),
              color 120ms cubic-bezier(0,0,0.2,1);
}
.xl-top__iconbtn:hover {
  background: var(--xl-color-surface-alt);
  border-color: rgba(46, 55, 69, 0.25);
  color: var(--xl-color-text);
}
.xl-top__iconbtn:focus-visible {
  outline: 2px solid var(--xl-color-focus);
  outline-offset: 2px;
}

.xl-top__user-wrap { position: relative }
.xl-top__user-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid var(--xl-color-border);
  background: var(--xl-color-bg);
  padding: 4px 10px 4px 4px;
  border-radius: var(--xl-radius-full);
  cursor: pointer;
  color: var(--xl-color-text);
  font-size: 13px;
  transition: border-color 120ms cubic-bezier(0,0,0.2,1);
}
.xl-top__user-btn:hover { border-color: rgba(46, 55, 69, 0.25) }
.xl-top__user-btn:focus-visible {
  outline: 2px solid var(--xl-color-focus);
  outline-offset: 2px;
}

.xl-top__menu {
  position: absolute;
  right: 0;
  top: calc(100% + 8px);
  min-width: 220px;
  background: var(--xl-color-bg);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg);
  box-shadow: var(--xl-shadow-lg);
  padding: 6px;
  z-index: 1200;
}
.xl-top__menu-hd {
  padding: 10px 12px 8px;
  border-bottom: 1px solid var(--xl-color-border);
  margin-bottom: 4px;
  font-size: 12px;
  color: var(--xl-color-text-secondary);
}
.xl-top__menu-hd strong {
  display: block;
  color: var(--xl-color-text);
  font-size: 13px;
}
.xl-top__menu-item {
  display: block;
  width: 100%;
  text-align: left;
  padding: 8px 12px;
  border: none;
  background: transparent;
  color: var(--xl-color-text);
  font-size: 13px;
  border-radius: var(--xl-radius-md);
  cursor: pointer;
  font-family: inherit;
}
.xl-top__menu-item:hover { background: var(--xl-color-surface-alt) }
.xl-top__menu-item--danger { color: var(--xl-color-danger) }
`;

export default function TopbarXL({ title = 'Panel de control', breadcrumbs }) {
  useEffect(() => {
    ensureKeyframes();
    injectCSS(CSS_ID, CSS);
  }, []);

  const [theme, setTheme] = useXLTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    const onEsc = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [menuOpen]);

  const username = (user && user.username) || 'Invitado';
  const email = (user && user.email) || '';
  const nextTheme = theme === 'dark' ? 'light' : 'dark';
  const themeLabel = theme === 'dark' ? 'Cambiar a claro' : 'Cambiar a oscuro';

  const handleLogout = () => {
    setMenuOpen(false);
    try { logout(); } catch (_) { /* sin sesion valida */ }
    navigate('/login', { replace: true });
  };

  return (
    <header className="xl-top" role="banner">
      <div className="xl-top__left">
        {breadcrumbs && breadcrumbs.length > 0 && (
          <XLBreadcrumbs items={breadcrumbs} />
        )}
        <h1 className="xl-top__title">{title}</h1>
      </div>

      <div className="xl-top__right">
        <CompanySelector />

        <XLTooltip label={themeLabel}>
          <button
            type="button"
            className="xl-top__iconbtn"
            onClick={() => setTheme(nextTheme)}
            aria-label={themeLabel}
          >
            {theme === 'dark' ? 'Claro' : 'Oscuro'}
          </button>
        </XLTooltip>

        <span className="xl-top__user-wrap" ref={menuRef}>
          <button
            type="button"
            className="xl-top__user-btn"
            onClick={() => setMenuOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={menuOpen ? 'true' : 'false'}
          >
            <XLAvatar name={username} size="sm" />
            <span>{username}</span>
          </button>
          {menuOpen && (
            <div
              className="xl-top__menu xl-motion-slide-down"
              role="menu"
              aria-label="Menu usuario"
            >
              <div className="xl-top__menu-hd">
                <strong>{username}</strong>
                {email && <span>{email}</span>}
              </div>
              <button
                type="button"
                className="xl-top__menu-item xl-top__menu-item--danger"
                onClick={handleLogout}
                role="menuitem"
              >
                Cerrar sesion
              </button>
            </div>
          )}
        </span>
      </div>
    </header>
  );
}
